import { useCallback, useEffect, useMemo, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';
import './AdminAvailability.css';

/**
 * Advisor Availability — capacity signal tab.
 *
 * Mounted at /admin/availability. Third tab in the Advisor & Pool
 * Management family alongside Routing Audit Log and Advisors & Pools.
 *
 * Reads /api/admin/advisor-availability (admin-gated) and shows, for the
 * next 14 and 30 days, what share of each advisor's bookable slots are
 * still open — open bookable slots / total bookable slots, where the
 * slot set is exactly what the booking flow would offer (real working
 * hours, weekdays, 24h lead-time floor). The aggregate drives a simple
 * signal against an editable threshold: when team availability drops
 * below it, that's the cue to ease marketing spend. For now it only
 * surfaces the signal — no budget action is automated.
 *
 * Working hours default to each advisor's real configuration; the
 * controls let an admin override the window uniformly for what-if
 * analysis without changing anyone's saved hours.
 */

interface AdvisorAvailability {
  id: string;
  fullName: string;
  email: string;
  open14: number;
  total14: number;
  pct14: number | null;
  open30: number;
  total30: number;
  pct30: number | null;
  nextSlotUtc: string | null;
  nextSlotDays: number | null;
  error?: string;
}

interface TeamAvailability {
  open14: number;
  total14: number;
  pct14: number | null;
  open30: number;
  total30: number;
  pct30: number | null;
  medianNextSlotDays: number | null;
}

interface AvailabilityApiResponse {
  ok: boolean;
  generatedAt?: string;
  leadMinutes?: number;
  usingOverride?: boolean;
  team?: TeamAvailability;
  advisors?: AdvisorAvailability[];
  error?: string;
  message?: string;
}

interface Settings {
  threshold: number; // percent
  leadMinutes: number;
  overrideOn: boolean;
  start: string; // HH:MM
  end: string; // HH:MM
  days: number[]; // 0..6
}

const LS_KEY = 'aa_advisor_availability_settings_v1';
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function defaultSettings(): Settings {
  return {
    threshold: 30,
    leadMinutes: 1440,
    overrideOn: false,
    start: '09:00',
    end: '17:00',
    days: [1, 2, 3, 4, 5],
  };
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...defaultSettings(), ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    /* ignore */
  }
  return defaultSettings();
}

function pctText(p: number | null): string {
  return p === null ? '—' : `${Math.round(p * 100)}%`;
}

function nextSlotText(days: number | null): string {
  if (days === null) return '>30d';
  if (days < 1) return `${Math.round(days * 24)}h`;
  return `${days.toFixed(1)}d`;
}

/** Threshold-relative color: red below, amber within 20pts above, green beyond. */
function bandColor(p: number | null, threshold: number): { bg: string; fg: string } {
  if (p === null) return { bg: '#eef1f5', fg: '#8C8C8C' };
  const v = p * 100;
  if (v < threshold) return { bg: '#fbe3e7', fg: '#C41E3A' };
  if (v < threshold + 20) return { bg: '#fff1d6', fg: '#9a6a00' };
  return { bg: '#e1f3e6', fg: '#1f7a3d' };
}

export function AdminAvailability() {
  const auth = useCurrentUser();

  const [team, setTeam] = useState<TeamAvailability | null>(null);
  const [advisors, setAdvisors] = useState<AdvisorAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [errBanner, setErrBanner] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [auth.status]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(settings));
    } catch {
      /* ignore */
    }
  }, [settings]);

  const refetch = useCallback(async () => {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = new URL('/api/admin/advisor-availability', window.location.origin);
      url.searchParams.set('lead', String(settings.leadMinutes));
      if (settings.overrideOn) {
        url.searchParams.set('start', settings.start);
        url.searchParams.set('end', settings.end);
        url.searchParams.set('days', settings.days.join(','));
      }
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = (await res.json()) as AvailabilityApiResponse;
      if (!res.ok || !json.ok) {
        setErrBanner(json.message ?? json.error ?? `HTTP ${res.status}`);
        return;
      }
      setTeam(json.team ?? null);
      setAdvisors(json.advisors ?? []);
      setLastUpdated(json.generatedAt ? new Date(json.generatedAt) : new Date());
      setErrBanner(null);
    } catch (e) {
      setErrBanner(e instanceof Error ? e.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [auth, settings]);

  // Initial load + reload whenever auth resolves. Control changes apply
  // on the explicit Recompute button so we don't refetch on every
  // keystroke.
  useEffect(() => {
    if (auth.status === 'authenticated') refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  const sortedAdvisors = useMemo(
    () =>
      [...advisors].sort((a, b) => {
        const av = a.pct14 ?? 2;
        const bv = b.pct14 ?? 2;
        return av - bv;
      }),
    [advisors],
  );

  const teamPct14 = team?.pct14 ?? null;
  const signal =
    teamPct14 === null
      ? null
      : teamPct14 * 100 < settings.threshold
        ? { cls: 'red', text: `Below ${settings.threshold}% — signal to ease marketing spend` }
        : teamPct14 * 100 < settings.threshold + 20
          ? { cls: 'amber', text: 'Tightening — watch closely' }
          : { cls: 'green', text: 'Healthy headroom' };

  function toggleDay(d: number) {
    setSettings((s) => ({
      ...s,
      days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d].sort(),
    }));
  }

  return (
    <div className="admin-app">
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1 className="admin-title">Advisor &amp; Pool Management</h1>
            <p className="admin-sub">
              Bookable-capacity signal — open slots vs. total slots over the next 14 &amp; 30 days
              {lastUpdated && (
                <>
                  {' · last updated '}
                  <strong>
                    {new Intl.DateTimeFormat('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                    }).format(lastUpdated)}
                  </strong>
                </>
              )}
            </p>
          </div>
        </div>
        <nav className="admin-nav">
          <a href="/admin/routing" className="admin-nav-link">
            Routing Audit Log
          </a>
          <a href="/admin/advisors" className="admin-nav-link">
            Advisors &amp; Pools
          </a>
          <a href="/admin/availability" className="admin-nav-link admin-nav-link-active">
            Advisor Availability
          </a>
          <a href="/admin/reminder-opt-outs" className="admin-nav-link">
            Reminder Opt-Outs
          </a>
        </nav>
      </header>

      <main className="admin-main">
        {errBanner && (
          <div className="admin-err" role="alert">
            <strong>Error:</strong> {errBanner}{' '}
            <button type="button" className="link-btn" onClick={() => setErrBanner(null)}>
              dismiss
            </button>
          </div>
        )}

        {/* Summary cards */}
        <div className="avail-cards">
          <div className="avail-card">
            <div className="avail-card-label">Next 14 days — team available</div>
            <div className="avail-card-big">{pctText(team?.pct14 ?? null)}</div>
            <div className="avail-card-foot">
              {team ? `${team.open14} open of ${team.total14} bookable slots` : ' '}
            </div>
            {signal && (
              <span className={`avail-signal avail-signal-${signal.cls}`}>{signal.text}</span>
            )}
          </div>
          <div className="avail-card">
            <div className="avail-card-label">Next 30 days — team available</div>
            <div className="avail-card-big">{pctText(team?.pct30 ?? null)}</div>
            <div className="avail-card-foot">
              {team ? `${team.open30} open of ${team.total30} bookable slots` : ' '}
            </div>
          </div>
          <div className="avail-card">
            <div className="avail-card-label">Soonest bookable slot (median)</div>
            <div className="avail-card-big">
              {nextSlotText(team?.medianNextSlotDays ?? null)}
            </div>
            <div className="avail-card-foot">
              Median days to each advisor&apos;s next opening (24h+ lead time)
            </div>
          </div>
        </div>

        {/* Controls */}
        <section className="avail-controls">
          <div className="avail-control">
            <label>Signal threshold (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={settings.threshold}
              onChange={(e) =>
                setSettings((s) => ({ ...s, threshold: Number(e.target.value) }))
              }
            />
          </div>
          <div className="avail-control">
            <label>Lead time (min)</label>
            <input
              type="number"
              min={0}
              step={60}
              value={settings.leadMinutes}
              onChange={(e) =>
                setSettings((s) => ({ ...s, leadMinutes: Number(e.target.value) }))
              }
            />
          </div>
          <div className="avail-control">
            <label>
              <input
                type="checkbox"
                checked={settings.overrideOn}
                onChange={(e) => setSettings((s) => ({ ...s, overrideOn: e.target.checked }))}
              />{' '}
              Override working hours
            </label>
          </div>
          {settings.overrideOn && (
            <>
              <div className="avail-control">
                <label>Start (ET)</label>
                <input
                  type="time"
                  value={settings.start}
                  onChange={(e) => setSettings((s) => ({ ...s, start: e.target.value }))}
                />
              </div>
              <div className="avail-control">
                <label>End (ET)</label>
                <input
                  type="time"
                  value={settings.end}
                  onChange={(e) => setSettings((s) => ({ ...s, end: e.target.value }))}
                />
              </div>
              <div className="avail-control">
                <label>Days</label>
                <div className="avail-days">
                  {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`avail-day ${settings.days.includes(d) ? 'on' : ''}`}
                      onClick={() => toggleDay(d)}
                    >
                      {DAY_NAMES[d]}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <button type="button" className="avail-recompute" disabled={loading} onClick={refetch}>
            {loading ? 'Computing…' : 'Recompute'}
          </button>
        </section>

        <p className="avail-note">
          Working hours default to each advisor&apos;s real configuration (the same hours the
          booking flow uses), falling back to 9am–5pm ET Mon–Fri. The override applies a uniform
          window for what-if analysis only — it never changes anyone&apos;s saved hours. Availability
          is a capacity signal, not an automated budget action.
        </p>

        {/* Per-advisor table */}
        {loading && advisors.length === 0 && !errBanner ? (
          <p className="admin-empty">Loading…</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table avail-table">
              <thead>
                <tr>
                  <th>Advisor</th>
                  <th>14d available</th>
                  <th>14d</th>
                  <th>30d available</th>
                  <th>Next bookable slot</th>
                  <th>Open / total (14d)</th>
                </tr>
              </thead>
              <tbody>
                {sortedAdvisors.map((a) => {
                  const c = bandColor(a.pct14, settings.threshold);
                  return (
                    <tr key={a.id}>
                      <td>
                        {a.fullName}
                        {a.error && <span className="avail-flag"> · calendar error</span>}
                      </td>
                      <td>
                        <span
                          className="avail-pill"
                          style={{ background: c.bg, color: c.fg }}
                        >
                          {pctText(a.pct14)}
                        </span>
                      </td>
                      <td className="avail-bar-cell">
                        <div className="avail-bar">
                          <span
                            style={{
                              width: `${a.pct14 === null ? 0 : Math.round(a.pct14 * 100)}%`,
                              background: c.fg,
                            }}
                          />
                        </div>
                      </td>
                      <td>{pctText(a.pct30)}</td>
                      <td>{nextSlotText(a.nextSlotDays)}</td>
                      <td>
                        {a.open14} / {a.total14}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
